/*
  File: foxtale-quiz.js
  Purpose: State machine + animation runtime for the Foxtale Quiz section.

  External integration:
    On email submit/skip, dispatches `foxtale-quiz:submit` on the section root with
    detail = { email, winner, secondary, resultType, counts }.
    Wire Klaviyo (or any other downstream) from a small project-specific listener.
*/

(function () {
  'use strict';

  // ── Utilities ────────────────────────────────────────────────────────────
  const ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, m => ESCAPE_MAP[m]);

  // ── Built-in emblem SVGs (per character) ─────────────────────────────────
  // Use currentColor for stroke/fill so the parent's color cascades in.
  function defaultEmblemSvg(key, size) {
    const sz = size || 90;
    const head = `<svg width="${sz}" height="${sz}" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">`;
    if (key === 'amy') {
      // Stylised sun: bright disk + 8 triangular wedge rays + 8 small spike rays
      const cx = 45, cy = 45;
      const wedges = [0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
        const rad = deg * Math.PI / 180;
        const inner = 21, outer = 36, halfBase = 4;
        const px = (r, off) => (cx + r * Math.cos(rad) + off * Math.cos(rad + Math.PI / 2)).toFixed(2);
        const py = (r, off) => (cy + r * Math.sin(rad) + off * Math.sin(rad + Math.PI / 2)).toFixed(2);
        return `<polygon points="${px(inner, -halfBase)},${py(inner, -halfBase)} ${px(inner, halfBase)},${py(inner, halfBase)} ${px(outer, 0)},${py(outer, 0)}" fill="currentColor" opacity="0.92"/>`;
      }).join('');
      const spikes = [22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5].map(deg => {
        const rad = deg * Math.PI / 180;
        const inner = 22, outer = 30, halfBase = 1.6;
        const px = (r, off) => (cx + r * Math.cos(rad) + off * Math.cos(rad + Math.PI / 2)).toFixed(2);
        const py = (r, off) => (cy + r * Math.sin(rad) + off * Math.sin(rad + Math.PI / 2)).toFixed(2);
        return `<polygon points="${px(inner, -halfBase)},${py(inner, -halfBase)} ${px(inner, halfBase)},${py(inner, halfBase)} ${px(outer, 0)},${py(outer, 0)}" fill="currentColor" opacity="0.55"/>`;
      }).join('');
      return head
        + '<circle cx="45" cy="45" r="17" fill="currentColor" opacity="1"/>'
        + '<circle cx="45" cy="45" r="19.5" stroke="currentColor" stroke-width="1" opacity="0.45"/>'
        + wedges + spikes
        + '</svg>';
    }
    if (key === 'dani') {
      return head
        + '<path d="M12 42 Q22 28 45 38 Q68 48 78 34" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" opacity="0.92"/>'
        + '<path d="M12 54 Q22 40 45 50 Q68 60 78 46" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.44"/>'
        + '<circle cx="45" cy="44" r="4.5" fill="currentColor" opacity="0.5"/>'
        + '</svg>';
    }
    if (key === 'eva') {
      return head
        + '<path d="M45 12 L48 36 L72 33 L52 45 L60 68 L45 54 L30 68 L38 45 L18 33 L42 36 Z" stroke="currentColor" stroke-width="1.4" fill="currentColor" fill-opacity="0.14" opacity="0.9"/>'
        + '<circle cx="22" cy="24" r="2.5" fill="currentColor" opacity="0.52"/>'
        + '<circle cx="68" cy="20" r="1.8" fill="currentColor" opacity="0.42"/>'
        + '<circle cx="72" cy="58" r="3"   fill="currentColor" opacity="0.35"/>'
        + '<circle cx="18" cy="64" r="1.8" fill="currentColor" opacity="0.28"/>'
        + '</svg>';
    }
    if (key === 'pax') {
      return head
        + '<path d="M45 46 Q28 28 16 30 Q8 34 14 46 Q20 56 45 55" stroke="currentColor" stroke-width="2" fill="currentColor" fill-opacity="0.13" stroke-linecap="round" stroke-linejoin="round" opacity="0.9"/>'
        + '<path d="M45 46 Q62 28 74 30 Q82 34 76 46 Q70 56 45 55" stroke="currentColor" stroke-width="2" fill="currentColor" fill-opacity="0.13" stroke-linecap="round" stroke-linejoin="round" opacity="0.9"/>'
        + '<path d="M45 55 Q43 64 45 72 Q47 64 45 55" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" opacity="0.5"/>'
        + '<circle cx="45" cy="46" r="3.5" fill="currentColor" opacity="0.82"/>'
        + '</svg>';
    }
    // kez default
    return head
      + '<path d="M54 12 L32 46 L46 46 L36 78 L66 36 L50 36 Z" stroke="currentColor" stroke-width="2" fill="currentColor" fill-opacity="0.22" stroke-linejoin="round" opacity="0.92"/>'
      + '</svg>';
  }

  function emblemSvgFor(key, charConfig, size) {
    if (charConfig && charConfig.emblemSvg && charConfig.emblemSvg.trim()) {
      // designer override
      return charConfig.emblemSvg;
    }
    return defaultEmblemSvg(key, size);
  }

  // Canonical character order. Used by both the constellation emblem row and
  // the mix-bar rows on the result page so they cannot drift.
  const CHAR_ORDER = ['dani', 'eva', 'pax', 'kez', 'amy'];

  // Single source of truth for a result emblem. Primary/blend/constellation
  // all render through this helper so the layer structure stays identical.
  // opts: { size, iconSize, rings } — rings defaults to 2.
  function renderEmblem(state, key, opts) {
    opts = opts || {};
    const cfg = (state.config.characters && state.config.characters[key]) || {};
    const color = cfg.accent || '#C89B4A';
    const size = opts.size;
    const iconSize = opts.iconSize || size;
    const rings = opts.rings || 2;
    const sizeStyle = size ? `;width:${size}px;height:${size}px;margin:0` : '';
    const ring3 = rings >= 3 ? '<div class="ftq-result-emblem-ring3"></div>' : '';
    return `<div class="ftq-result-emblem" style="--ftq-char-color:${esc(color)}${sizeStyle}">
      <div class="ftq-result-emblem-bg"></div>
      <div class="ftq-result-emblem-ring1"></div>
      <div class="ftq-result-emblem-ring2"></div>
      ${ring3}
      <div class="ftq-result-emblem-icon">${emblemSvgFor(key, cfg, iconSize)}</div>
    </div>`;
  }

  function featherSvg() {
    return ''
      + '<svg width="48" height="62" viewBox="0 0 48 62" fill="none" xmlns="http://www.w3.org/2000/svg">'
      + '<path d="M24 5 C30 18 32 38 24 57" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" opacity="0.9"/>'
      + '<path d="M24 12 Q38 7 42 14"  stroke="currentColor" stroke-width="1"    stroke-linecap="round" opacity="0.75"/>'
      + '<path d="M24 18 Q37 13 41 20" stroke="currentColor" stroke-width="1"    stroke-linecap="round" opacity="0.68"/>'
      + '<path d="M24 24 Q37 20 40 27" stroke="currentColor" stroke-width="0.9"  stroke-linecap="round" opacity="0.60"/>'
      + '<path d="M24 30 Q36 26 39 33" stroke="currentColor" stroke-width="0.9"  stroke-linecap="round" opacity="0.52"/>'
      + '<path d="M24 36 Q35 33 37 40" stroke="currentColor" stroke-width="0.8"  stroke-linecap="round" opacity="0.44"/>'
      + '<path d="M24 42 Q33 40 34 46" stroke="currentColor" stroke-width="0.8"  stroke-linecap="round" opacity="0.35"/>'
      + '<path d="M24 12 Q12 8 10 15"  stroke="currentColor" stroke-width="1"    stroke-linecap="round" opacity="0.5"/>'
      + '<path d="M24 18 Q13 14 11 21" stroke="currentColor" stroke-width="1"    stroke-linecap="round" opacity="0.44"/>'
      + '<path d="M24 24 Q14 21 13 28" stroke="currentColor" stroke-width="0.9"  stroke-linecap="round" opacity="0.38"/>'
      + '<path d="M24 30 Q16 28 15 34" stroke="currentColor" stroke-width="0.85" stroke-linecap="round" opacity="0.30"/>'
      + '<path d="M24 36 Q17 35 17 41" stroke="currentColor" stroke-width="0.8"  stroke-linecap="round" opacity="0.22"/>'
      + '</svg>';
  }

  const arrowSvg = '<svg width="14" height="10" viewBox="0 0 14 10" fill="none"><path d="M1 5 L13 5 M9 1 L13 5 L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ── Fireflies ────────────────────────────────────────────────────────────
  // Keyframes are shared across the page (single style tag). Wander param drives lateral amplitude.
  function injectFireflyKeyframes(wander) {
    const w = wander / 28;
    const t = (x, y) => `translate(${(x * w).toFixed(1)}px,${y}vh)`;
    const kf = `
      @keyframes ftq-drift1 {
        0%   { opacity: 0; transform: ${t(0, 0)}; }
        8%   { opacity: 1; }
        12%  { transform: ${t(-8, -7)}; }
        20%  { transform: ${t(-14, -12)}; }
        28%  { transform: ${t(-10, -9)}; }
        38%  { transform: ${t(-18, -26)}; }
        46%  { transform: ${t(-22, -30)}; }
        54%  { transform: ${t(-16, -28)}; }
        64%  { transform: ${t(-24, -48)}; }
        74%  { transform: ${t(-18, -62)}; }
        84%  { transform: ${t(-10, -74)}; }
        88%  { opacity: 1; }
        95%  { opacity: 0; }
        100% { opacity: 0; transform: ${t(-4, -105)}; }
      }
      @keyframes ftq-drift2 {
        0%   { opacity: 0; transform: ${t(0, 0)}; }
        8%   { opacity: 1; }
        12%  { transform: ${t(10, -5)}; }
        20%  { transform: ${t(18, -14)}; }
        28%  { transform: ${t(14, -11)}; }
        38%  { transform: ${t(22, -28)}; }
        46%  { transform: ${t(8, -34)}; }
        54%  { transform: ${t(16, -32)}; }
        64%  { transform: ${t(24, -50)}; }
        74%  { transform: ${t(14, -62)}; }
        84%  { transform: ${t(20, -76)}; }
        88%  { opacity: 1; }
        95%  { opacity: 0; }
        100% { opacity: 0; transform: ${t(12, -105)}; }
      }
      @keyframes ftq-drift3 {
        0%   { opacity: 0; transform: ${t(0, 0)}; }
        8%   { opacity: 1; }
        12%  { transform: ${t(16, -8)}; }
        20%  { transform: ${t(-4, -16)}; }
        28%  { transform: ${t(-10, -14)}; }
        38%  { transform: ${t(6, -30)}; }
        46%  { transform: ${t(-12, -38)}; }
        54%  { transform: ${t(-6, -36)}; }
        64%  { transform: ${t(10, -52)}; }
        72%  { transform: ${t(-8, -64)}; }
        82%  { transform: ${t(4, -76)}; }
        88%  { opacity: 1; }
        95%  { opacity: 0; }
        100% { opacity: 0; transform: ${t(2, -105)}; }
      }
      @keyframes ftq-drift4 {
        0%   { opacity: 0; transform: ${t(0, 0)}; }
        8%   { opacity: 1; }
        14%  { transform: ${t(-16, -10)}; }
        24%  { transform: ${t(-20, -7)}; }
        34%  { transform: ${t(-12, -22)}; }
        44%  { transform: ${t(4, -30)}; }
        52%  { transform: ${t(8, -28)}; }
        62%  { transform: ${t(-6, -44)}; }
        72%  { transform: ${t(-14, -58)}; }
        82%  { transform: ${t(-8, -70)}; }
        88%  { opacity: 1; }
        95%  { opacity: 0; }
        100% { opacity: 0; transform: ${t(-4, -105)}; }
      }
      @keyframes ftq-ffBlink {
        0%   { opacity: 0.12; }
        4%   { opacity: 0.12; }
        5%   { opacity: 1; }
        9%   { opacity: 0.22; }
        12%  { opacity: 0.12; }
        100% { opacity: 0.12; }
      }
    `;
    let el = document.getElementById('ftq-firefly-kf');
    if (!el) { el = document.createElement('style'); el.id = 'ftq-firefly-kf'; document.head.appendChild(el); }
    el.textContent = kf;
  }

  const FIREFLY_PALETTE = [[210,240,70],[190,235,55],[235,225,60],[215,250,75],[200,215,45]];

  function makeFirefly(cfg, nearCamera) {
    const drifts = ['ftq-drift1', 'ftq-drift2', 'ftq-drift3', 'ftq-drift4'];
    const s = nearCamera ? Math.random() * 2 + 2 : Math.random() * 2.5 + 1.5;
    const [r, g, b] = FIREFLY_PALETTE[Math.floor(Math.random() * FIREFLY_PALETTE.length)];
    const drft = drifts[Math.floor(Math.random() * drifts.length)];
    const dur = ((Math.random() * 18 + 16) * cfg.speed).toFixed(1);
    const del = -(Math.random() * parseFloat(dur));
    const gm = nearCamera ? cfg.glow * 1.3 : cfg.glow;
    const bDur = Number(cfg.blinkRate).toFixed(2);
    const bDel = (Math.random() * cfg.blinkRate).toFixed(2);

    const outer = document.createElement('div');
    outer.className = 'ftq-p';
    outer.style.cssText = [
      `left:${(Math.random() * 100).toFixed(1)}%`,
      `bottom:${(-Math.random() * 8).toFixed(1)}%`,
      `animation:${drft} ${dur}s ${del.toFixed(1)}s linear infinite`,
    ].join(';');

    const inner = document.createElement('div');
    inner.style.cssText = [
      'position:absolute',
      'top:0',
      'left:0',
      `width:${s.toFixed(1)}px`,
      `height:${s.toFixed(1)}px`,
      'border-radius:50%',
      `background:rgba(${r},${g},${b},1)`,
      'box-shadow:'
        + `0 0 ${(s * 2 * gm).toFixed(1)}px ${(s * gm).toFixed(1)}px rgba(${r},${g},${b},0.7),`
        + `0 0 ${(s * 6 * gm).toFixed(1)}px ${(s * 2.5 * gm).toFixed(1)}px rgba(${r},${g},${b},0.28),`
        + `0 0 ${(s * 14 * gm).toFixed(1)}px ${(s * 4 * gm).toFixed(1)}px rgba(${r},${g},${b},0.1)`,
      `animation:ftq-ffBlink ${bDur}s ${bDel}s ease-in-out infinite`,
    ].join(';');
    inner.textContent = '​'; // defeat global div:empty { display: none }
    outer.appendChild(inner);
    return outer;
  }

  function respawnFireflies(root, cfg) {
    injectFireflyKeyframes(cfg.wander);
    const mid = root.querySelector('.ftq-flies-mid');
    const near = root.querySelector('.ftq-flies-near');
    mid.innerHTML = '';
    near.innerHTML = '';

    const total = cfg.count;
    const nearCount = Math.max(1, Math.round(total * 0.18));
    const midCount = total - nearCount;

    for (let i = 0; i < midCount;  i++) mid.appendChild(makeFirefly(cfg, false));
    for (let i = 0; i < nearCount; i++) near.appendChild(makeFirefly(cfg, true));
  }

  // ── Card glow tracker ────────────────────────────────────────────────────
  function startCardGlowTracker(root) {
    const glowState = new WeakMap();
    let tick = 0;
    let stopped = false;

    function loop() {
      if (stopped) return;
      requestAnimationFrame(loop);
      if (++tick % 2 !== 0) return;

      const tiles = root.querySelectorAll('.ftq-tile');
      if (!tiles.length) return;

      const dots = root.querySelectorAll('.ftq-flies-mid .ftq-p > div');
      if (!dots.length) return;

      const pts = [];
      dots.forEach(d => {
        const r = d.getBoundingClientRect();
        if (r.width > 0) pts.push({ x: r.left + r.width * 0.5, y: r.top + r.height * 0.5 });
      });
      if (!pts.length) return;

      tiles.forEach(card => {
        const cr = card.getBoundingClientRect();
        const cx = cr.left + cr.width * 0.5;
        const cy = cr.top + cr.height * 0.5;

        let bestDist = Infinity, bx = cx, by = cy;
        for (let i = 0; i < pts.length; i++) {
          const d = Math.hypot(pts[i].x - cx, pts[i].y - cy);
          if (d < bestDist) { bestDist = d; bx = pts[i].x; by = pts[i].y; }
        }

        const tx = (bx - cr.left) / cr.width * 100;
        const ty = (by - cr.top)  / cr.height * 100;

        if (!glowState.has(card)) glowState.set(card, { x: 85, y: 18 });
        const s = glowState.get(card);
        s.x += (tx - s.x) * 0.028;
        s.y += (ty - s.y) * 0.028;

        const influence = Math.max(0, 1 - bestDist / 350);
        const opacity = (0.25 + influence * 0.28).toFixed(2);

        card.style.setProperty('--glow-x', `${s.x.toFixed(1)}%`);
        card.style.setProperty('--glow-y', `${s.y.toFixed(1)}%`);
        card.style.setProperty('--glow-opacity', opacity);
      });
    }
    requestAnimationFrame(loop);
    return { stop() { stopped = true; } };
  }

  // ── Firefly burst (result reveal) ────────────────────────────────────────
  function spawnFireflyBurst(originEl, parentEl) {
    if (!originEl) return;
    const parent = parentEl || document.body;
    const rect = originEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const particles = [];

    for (let i = 0; i < 22; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.6 + Math.random() * 0.9;
      const [r, g, b] = FIREFLY_PALETTE[Math.floor(Math.random() * FIREFLY_PALETTE.length)];
      const size = 1.6 + Math.random() * 2.4;
      const gm = 1.8;
      const lifetime = 4500 + Math.random() * 3000;
      const startDelay = Math.random() * 600;

      const el = document.createElement('div');
      el.textContent = '​'; // defeat global div:empty { display: none }
      el.style.cssText = [
        'position:fixed',
        `left:${(cx - size / 2).toFixed(1)}px`,
        `top:${(cy - size / 2).toFixed(1)}px`,
        `width:${size.toFixed(1)}px`,
        `height:${size.toFixed(1)}px`,
        'border-radius:50%',
        `background:rgba(${r},${g},${b},1)`,
        'box-shadow:'
          + `0 0 ${(size * 2 * gm).toFixed(1)}px ${(size * gm).toFixed(1)}px rgba(${r},${g},${b},0.65),`
          + `0 0 ${(size * 5 * gm).toFixed(1)}px ${(size * 2 * gm).toFixed(1)}px rgba(${r},${g},${b},0.2)`,
        'pointer-events:none',
        'z-index:5',
        'opacity:0',
        'will-change:transform,opacity',
      ].join(';');
      parent.appendChild(el);

      particles.push({
        el,
        x: 0, y: 0,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        drag: 0.990 + Math.random() * 0.006,
        buoyancy: -0.001 - Math.random() * 0.002,
        wobble: (Math.random() - 0.5) * 0.044,
        born: performance.now() + startDelay,
        lifetime,
        dead: false,
      });

      setTimeout(() => el.remove(), lifetime + startDelay + 600);
    }

    function step(now) {
      let alive = 0;
      particles.forEach(p => {
        if (p.dead) return;
        const age = now - p.born;
        if (age < 0) { p.el.style.opacity = 0; alive++; return; }
        const t = age / p.lifetime;
        if (t >= 1) { p.dead = true; p.el.style.opacity = 0; return; }
        alive++;

        p.vy += p.buoyancy;
        p.vx += p.wobble;
        p.vx *= p.drag;
        p.vy *= p.drag;
        p.x += p.vx;
        p.y += p.vy;

        let o;
        if (t < 0.07) o = t / 0.07;
        else if (t < 0.65) o = 1;
        else o = 1 - (t - 0.65) / 0.35;
        p.el.style.opacity = o;
        p.el.style.transform = `translate(${p.x.toFixed(1)}px,${p.y.toFixed(1)}px)`;
      });
      if (alive > 0) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  // ── Scoring ──────────────────────────────────────────────────────────────
  function tally(answers, questions) {
    const counts = { dani: 0, eva: 0, pax: 0, kez: 0, amy: 0 };
    questions.forEach((q, i) => {
      const idx = answers[i];
      if (idx != null && q.answers[idx]) counts[q.answers[idx].char]++;
    });
    return counts;
  }

  function getLeader(answers, questions) {
    const counts = tally(answers, questions);
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0][1] > 0 ? sorted[0][0] : null;
  }

  function calcResult(answers, questions) {
    const counts = tally(answers, questions);
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    let winner = sorted[0][0];
    let secondary = sorted[1][0];

    // Tiebreaker: Q10 (index 9) settles the top slot
    if (sorted[0][1] === sorted[1][1] && answers[9] != null) {
      const a = questions[9].answers[answers[9]];
      if (a) {
        winner = a.char;
        secondary = a.char === sorted[0][0] ? sorted[1][0] : sorted[0][0];
      }
    }

    const top = counts[winner];
    const sec = counts[secondary];
    let resultType;
    if (top >= 6)                    resultType = 'primary';
    else if (top >= 4 && sec >= 3)   resultType = 'blend';
    else                             resultType = 'constellation';

    return { winner, secondary, resultType, counts };
  }

  function blendKeyFor(a, b) {
    return [a, b].sort().join('-');
  }

  function pickWhisper(charKey, charsByKey) {
    const c = charsByKey[charKey];
    if (!c || !c.whispers || !c.whispers.length) return null;
    return c.whispers[Math.floor(Math.random() * c.whispers.length)];
  }

  // ── DOM helpers ──────────────────────────────────────────────────────────
  function setProgress(state, pct) {
    if (state.progress) state.progress.style.width = pct + '%';
  }

  function setBackVisible(state, show) {
    if (!state.backBtn) return;
    if (show) state.backBtn.removeAttribute('hidden');
    else state.backBtn.setAttribute('hidden', '');
  }

  function setScreen(state, screen) {
    if (state.forest) state.forest.dataset.screen = screen;
  }

  function setLantern(state, charKey) {
    const lantern = state.lantern;
    if (!lantern) return;
    const charConfig = state.config.characters && state.config.characters[charKey];
    if (!charConfig) {
      lantern.style.background = 'radial-gradient(ellipse 60% 70% at 50% 42%, rgba(248,232,196,0.06) 0%, transparent 65%)';
      return;
    }
    // Convert accent hex to rgba @ 0.18 for the lantern wash
    const tint = hexToRgba(charConfig.accent, 0.18) || 'rgba(248,232,196,0.06)';
    lantern.style.background = `radial-gradient(ellipse 60% 70% at 50% 42%, ${tint} 0%, rgba(248,232,196,0.04) 40%, transparent 65%)`;
  }

  function hexToRgba(hex, alpha) {
    if (!hex) return null;
    const m = String(hex).trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
  }

  // ── Renderers ────────────────────────────────────────────────────────────
  // SVGs for the avatar intro
  const FOX_MARK_SVG = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">'
    + '<path d="M3.5 5L9 12.5L3.5 16L9 23L14 17.5L19 23L24.5 16L19 12.5L24.5 5L17.5 8.5L14 3.5L10.5 8.5L3.5 5Z" stroke="#C89B4A" stroke-width="1.1" stroke-linejoin="round" fill="none" opacity="0.7"/>'
    + '<circle cx="10.5" cy="13.5" r="1.3" fill="#C89B4A" opacity="0.6"/>'
    + '<circle cx="17.5" cy="13.5" r="1.3" fill="#C89B4A" opacity="0.6"/>'
    + '</svg>';
  const TAP_ARROW_SVG = '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">'
    + '<path d="M1.5 4.5h6M5.5 2.5l2 2-2 2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/>'
    + '</svg>';

  function renderIntro(state) {
    const portraits = state.config.portraits || {};
    const ORDER = ['dani', 'pax', 'amy', 'eva', 'kez'];
    const cards = ORDER.map(key => {
      const c = state.charsByKey[key];
      if (!c) return '';
      const portrait = portraits[key] || '';
      const glow = c.glow || 'rgba(200,155,74,0.20)';
      return `
        <div class="ftq-avi-card" id="ftq-avi-card-${esc(key)}" data-ftq-action="avi-toggle" data-avi-id="${esc(key)}">
          <div class="ftq-avi-closed">
            <div class="ftq-avi-ring">
              <img src="${esc(portrait)}" alt="" width="76" height="76" loading="lazy" decoding="async"/>
            </div>
            <div class="ftq-avi-text-closed">
              <div class="ftq-avi-name-sm">${esc(c.name)}</div>
              <span class="ftq-avi-everyone-label">Everyone thinks</span>
              <p class="ftq-avi-everyone-sm">${esc(c.everyoneThinks || '')}</p>
            </div>
            <div class="ftq-avi-tap-hint">${TAP_ARROW_SVG}<span>Fox sees</span></div>
          </div>
          <div class="ftq-avi-open" id="ftq-avi-open-${esc(key)}">
            <div class="ftq-avi-hero">
              <div class="ftq-avi-hero-glow" style="background: radial-gradient(ellipse 90% 70% at 50% 0%, ${esc(glow)} 0%, transparent 65%);"></div>
              <img src="${esc(portrait)}" alt="" loading="lazy" decoding="async"/>
              <div class="ftq-avi-hero-fade"></div>
              <div class="ftq-avi-hero-nameplate">
                <div class="ftq-avi-archetype">${esc(c.archetype || '')}</div>
                <div class="ftq-avi-open-name">${esc(c.name)}</div>
              </div>
            </div>
            <div class="ftq-avi-body">
              <div class="ftq-avi-everyone-block">
                <span class="ftq-avi-row-label">Everyone thinks</span>
                <p class="ftq-avi-everyone-text">${esc(c.everyoneThinks || '')}</p>
              </div>
              <div class="ftq-avi-divider"></div>
              <div class="ftq-avi-fox-block">
                <span class="ftq-avi-fox-eyebrow">The Fox sees</span>
                <p class="ftq-avi-fox-truth">${esc(c.foxSees || '')}</p>
              </div>
            </div>
            <div class="ftq-avi-close-row">
              <button type="button" class="ftq-avi-close-btn" data-ftq-action="avi-close" data-avi-id="${esc(key)}">close ↑</button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="ftq-intro-avi">
        <div class="ftq-avi-header">
          <div class="ftq-avi-fox-mark">${FOX_MARK_SVG}</div>
          <div class="ftq-avi-eyebrow">Foxtale Forest</div>
          <h1 class="ftq-avi-hed">Meet The Foxtale <em>Girls</em></h1>
          <div class="ftq-avi-rule"></div>
          <p class="ftq-avi-intro-body">Everyone sees one side of her. The Fox sees another.</p>
          <div class="ftq-avi-intro-hint"><span>tap each girl to reveal</span></div>
        </div>
        <div class="ftq-avi-cards-list">${cards}</div>
        <div class="ftq-avi-bottom-cta">
          <p class="ftq-avi-cta-meta">Now that you've met them — which one feels most like you?</p>
          <button type="button" class="ftq-avi-btn-quiz" data-ftq-action="start">
            <span class="ftq-avi-btn-quiz-label">Enter the Forest</span>
            <span class="ftq-avi-btn-quiz-sub">follow the path</span>
          </button>
        </div>
      </div>
    `;
  }

  function renderQuestion(state) {
    const total = state.content.questions.length;
    const q = state.content.questions[state.qIdx];
    const selIdx = state.answers[state.qIdx];
    const isLast = state.qIdx === total - 1;
    const slideClass = state.dir === 'back' ? 'ftq-q-enter-back' : 'ftq-q-enter';

    const tiles = q.answers.map((a, i) => `
      <button type="button" class="ftq-tile ${i === selIdx ? 'is-selected' : ''}" data-ftq-action="select" data-idx="${i}" role="radio" aria-checked="${i === selIdx ? 'true' : 'false'}">
        <span class="ftq-tile-radio" aria-hidden="true"></span>
        <div class="ftq-tile-txt">${esc(a.text)}</div>
        ${i === selIdx ? '<div class="ftq-tile-check">✦</div>' : ''}
      </button>
    `).join('');

    const continueLabel = isLast ? 'The Fox has seen enough' : 'Continue';
    const activeClass = selIdx != null ? 'is-active' : '';

    return `
      <div class="ftq-q-wrap ${slideClass}">
        <div class="ftq-scene">
          ${q.scene.image ? `<img class="ftq-scene-icon" src="${esc(q.scene.image)}" alt="" width="72" height="72" loading="lazy" decoding="async">` : `<span class="ftq-scene-emoji">${esc(q.scene.emoji || '')}</span>`}
          <span class="ftq-scene-name">${esc(q.scene.name)}</span>
        </div>
        <div class="ftq-q-text">${esc(q.text)}</div>
        <div class="ftq-answers" role="radiogroup" aria-label="${esc(q.scene.name)}">${tiles}</div>
        <div class="ftq-whisper-host"></div>
      </div>
      <button type="button" class="ftq-btn-continue ${activeClass}" data-ftq-action="next" ${selIdx == null ? 'disabled' : ''}>
        ${esc(continueLabel)} ${arrowSvg}
      </button>
    `;
  }

  function renderEmail(state) {
    const c = state.config.copy.emailGate;
    const winner = getLeader(state.answers, state.content.questions);
    const charCfg = winner ? ((state.config.characters && state.config.characters[winner]) || {}) : null;
    const floodColor = charCfg ? (charCfg.flood || 'rgba(200,155,74,0.18)') : 'rgba(200,155,74,0.18)';

    const teaserHtml = charCfg ? `
      <div class="ftq-email-teaser" aria-hidden="true">
        <div class="ftq-email-teaser-aura" style="background:radial-gradient(circle,${floodColor} 0%,transparent 68%)"></div>
        ${renderEmblem(state, winner, { size: 118, iconSize: 84, rings: 3 })}
      </div>
      <div class="ftq-email-divider" aria-hidden="true">
        <div class="ftq-email-divider-line"></div>
        <span class="ftq-email-divider-sym">✦</span>
        <div class="ftq-email-divider-line"></div>
      </div>
    ` : '';

    return `
      <div class="ftq-email">
        ${teaserHtml}
        <form data-ftq-action="email-submit" novalidate>
          <div class="ftq-email-eyebrow">${esc(c.eyebrow)}</div>
          <h2 class="ftq-email-title">${esc(c.title)}</h2>
          <p class="ftq-email-body">${esc(c.body)}</p>
          <div class="ftq-email-field">
            <input
              type="email"
              class="ftq-email-input"
              placeholder="Your email address"
              autocomplete="email"
              required
            />
          </div>
          <p class="ftq-email-error" hidden></p>
          <button type="submit" class="ftq-email-submit">
            ${esc(c.submitLabel)} ${arrowSvg}
          </button>
          <button type="button" class="ftq-email-skip" data-ftq-action="email-skip">${esc(c.skipLabel)}</button>
        </form>
      </div>
    `;
  }

  function renderResult(state) {
    const r = state.config.copy.result;
    const result = state._previewResult || calcResult(state.answers, state.content.questions);
    state._result = result;
    const winner = state.charsByKey[result.winner];
    const secondary = state.charsByKey[result.secondary];

    const eyebrow = result.resultType === 'blend' ? r.eyebrowBlend
      : result.resultType === 'constellation' ? r.eyebrowConstellation
      : r.eyebrowPrimary;
    const badge = result.resultType === 'blend' ? r.badgeBlend
      : result.resultType === 'constellation' ? r.badgeConstellation
      : r.badgePrimary;

    const winnerCfg = state.config.characters && state.config.characters[result.winner] || {};
    const secCfg = state.config.characters && state.config.characters[result.secondary] || {};

    // Look up bespoke blend / constellation copy
    const blendData = result.resultType === 'blend'
      ? (state.content.blends && state.content.blends[blendKeyFor(result.winner, result.secondary)]) || null
      : null;
    const constellationData = result.resultType === 'constellation'
      ? state.content.constellation || null
      : null;

    // ── Emblem ──────────────────────────────────────────────────────────
    // All three result types render through renderEmblem() so the layer
    // structure stays identical. Only size/count/wrapper vary per type.
    let emblemHtml;
    if (result.resultType === 'blend') {
      emblemHtml = `<div class="ftq-blend-emblems">
        ${renderEmblem(state, result.winner,    { size: 88, iconSize: 72 })}
        <span class="ftq-blend-and" aria-label="and">+</span>
        ${renderEmblem(state, result.secondary, { size: 88, iconSize: 72 })}
      </div>`;
    } else if (result.resultType === 'constellation') {
      emblemHtml = `<div class="ftq-blend-emblems ftq-constellation-emblems">
        ${CHAR_ORDER.map(k => renderEmblem(state, k, { size: 56, iconSize: 44 })).join('')}
      </div>`;
    } else {
      emblemHtml = renderEmblem(state, result.winner, { iconSize: 90 });
    }

    // ── Identity (name + archetype) ─────────────────────────────────────
    let identityHtml;
    if (result.resultType === 'blend' && blendData) {
      identityHtml = `<div class="ftq-blend-names">
          <span class="ftq-blend-name" style="color:${esc(winnerCfg.accent || '#F7F3E9')}">${esc(winner.name)}</span>
          <span class="ftq-blend-name-sep">&amp;</span>
          <span class="ftq-blend-name" style="color:${esc(secCfg.accent || '#F7F3E9')}">${esc(secondary.name)}</span>
        </div>
        <p class="ftq-result-archetype">${esc(blendData.archetype)}</p>`;
    } else if (result.resultType === 'blend') {
      // fallback: no bespoke blend found, show both archetypes
      identityHtml = `<div class="ftq-blend-names">
          <span class="ftq-blend-name" style="color:${esc(winnerCfg.accent || '#F7F3E9')}">${esc(winner.name)}</span>
          <span class="ftq-blend-name-sep">&amp;</span>
          <span class="ftq-blend-name" style="color:${esc(secCfg.accent || '#F7F3E9')}">${esc(secondary.name)}</span>
        </div>
        <div class="ftq-blend-archs">
          <span class="ftq-blend-arch" style="color:${esc(winnerCfg.accent || '#E6B55A')}">${esc(winner.archetype)}</span>
          <span class="ftq-blend-arch-sep">·</span>
          <span class="ftq-blend-arch" style="color:${esc(secCfg.accent || '#E6B55A')}">${esc(secondary.archetype)}</span>
        </div>`;
    } else if (result.resultType === 'constellation' && constellationData) {
      identityHtml = `<h1 class="ftq-result-name">${esc(constellationData.archetype)}</h1>`;
    } else {
      identityHtml = `<h1 class="ftq-result-name" style="color:${esc(winnerCfg.accent || '#FFFFFF')}">${esc(winner.name)}</h1>
        <p class="ftq-result-archetype">${esc(winner.archetype)}</p>`;
    }

    // ── Tagline + story body ────────────────────────────────────────────
    let tagline, storyText;
    if (result.resultType === 'blend' && blendData) {
      tagline = blendData.tagline;
      storyText = blendData.story;
    } else if (result.resultType === 'constellation' && constellationData) {
      tagline = constellationData.tagline;
      storyText = constellationData.story;
    } else {
      tagline = winner.tagline;
      storyText = winner.story;
    }
    const storyParagraphs = (storyText || '').split('\n\n').map(p => `<p>${esc(p)}</p>`).join('');

    // Constellation: tint the story rule using the winner accent (just for some color)
    const storyAccent = winnerCfg.accent || '#C89B4A';

    // No secondary-block stitching anymore — bespoke blend copy replaces it
    const secondaryStoryHtml = '';
    const givingHtml = '';

    const charOrder = CHAR_ORDER;
    const totalCount = charOrder.reduce((sum, k) => sum + (result.counts[k] || 0), 0);
    const isHighlit = (k) => {
      if (result.resultType === 'primary') return k === result.winner;
      if (result.resultType === 'blend')   return k === result.winner || k === result.secondary;
      return result.counts[k] > 0;
    };
    const mixRows = charOrder.map(k => {
      const c = state.charsByKey[k];
      const cc = state.config.characters && state.config.characters[k] || {};
      const pct = totalCount > 0 ? Math.round((result.counts[k] / totalCount) * 100) : 0;
      return `
        <div class="ftq-mix-row">
          <div class="ftq-mix-lbl">
            <span class="ftq-mix-lbl-name">${esc(c.name)}</span>
            <span class="ftq-mix-lbl-arch">${esc(c.archetype)}</span>
          </div>
          <div class="ftq-mix-rail">
            <div class="ftq-mix-bar"
                 data-target-width="${pct}"
                 style="background:${esc(cc.mixBar || cc.accent || '#C89B4A')};opacity:${isHighlit(k) ? 1 : 0.35}"></div>
          </div>
          <span class="ftq-mix-pct">${pct}%</span>
        </div>
      `;
    }).join('');

    const shopHref = 'https://www.findingfoxtale.com/pages/foxtaleforest';

    const portraits = state.config.portraits || {};
    const portraitFor = (key, cfg) => {
      const src = portraits[key];
      if (!src) return '';
      return `<div class="ftq-result-portrait-wrap" style="--ftq-char-color:${esc(cfg.accent || '#C89B4A')}">
           <div class="ftq-result-portrait-glow" aria-hidden="true"></div>
           <img class="ftq-result-portrait-img" src="${esc(src)}" alt="" decoding="async">
         </div>`;
    };
    let portraitHtml = '';
    if (result.resultType === 'primary') {
      portraitHtml = portraitFor(result.winner, winnerCfg);
    } else if (result.resultType === 'blend') {
      const pair = portraitFor(result.winner, winnerCfg) + portraitFor(result.secondary, secCfg);
      portraitHtml = pair ? `<div class="ftq-result-portrait-pair">${pair}</div>` : '';
    }

    return `
      <div class="ftq-result" data-phase="0">
        ${portraitHtml}
        ${emblemHtml}

        <div class="ftq-result-identity ftq-phase-1">
          <p class="ftq-result-eyebrow">${esc(eyebrow)}</p>
          ${identityHtml}
          <p class="ftq-result-badge">${esc(badge)}</p>
        </div>

        <div class="ftq-result-body ftq-phase-2">
          <div class="ftq-result-pill" style="--ftq-char-color:${esc(winnerCfg.accent || '#E6B55A')}">
            <p>${esc(tagline)}</p>
          </div>

          <div class="ftq-result-story" style="--ftq-char-color:${esc(storyAccent)}">
            ${storyParagraphs}
            ${secondaryStoryHtml}
            <div class="ftq-result-story-feather">${featherSvg()}</div>
          </div>

          ${givingHtml}

          <div class="ftq-mix">
            <p class="ftq-mix-head">Your character mix</p>
            ${mixRows}
          </div>

          <a class="ftq-btn-shop" href="${esc(shopHref)}" data-ftq-action="shop">Reveal More ${arrowSvg}</a>
          <button type="button" class="ftq-btn-restart" data-ftq-action="restart">↩ ${esc(r.restartLabel || 'Walk the path again')}</button>
        </div>
      </div>
    `;
  }

  // ── Render dispatch + listener binding ───────────────────────────────────
  function renderCurrent(state) {
    if (state.screen === 'intro') {
      state.stage.innerHTML = renderIntro(state);
      setProgress(state, 0);
      setScreen(state, 'intro');
      setBackVisible(state, false);
      setLantern(state, null);
    } else if (state.screen === 'quiz') {
      state.stage.innerHTML = renderQuestion(state);
      const total = state.content.questions.length;
      setProgress(state, (state.qIdx / total) * 100);
      setScreen(state, 'question');
      setBackVisible(state, true);
      setLantern(state, getLeader(state.answers, state.content.questions));
    } else if (state.screen === 'email') {
      state.stage.innerHTML = renderEmail(state);
      setProgress(state, 100);
      setScreen(state, 'email');
      setBackVisible(state, false);
    } else if (state.screen === 'result') {
      state.stage.innerHTML = renderResult(state);
      setProgress(state, 100);
      setScreen(state, 'result');
      setBackVisible(state, false);
      startResultPhases(state);
    }
    bindStageEvents(state);
  }

  function bindStageEvents(state) {
    state.stage.querySelectorAll('[data-ftq-action]').forEach(el => {
      const action = el.dataset.ftqAction;
      if (action === 'start')        el.addEventListener('click', () => transition(state, () => { state.screen = 'quiz'; }));
      else if (action === 'select')  el.addEventListener('click', () => onSelect(state, parseInt(el.dataset.idx, 10)));
      else if (action === 'next')    el.addEventListener('click', () => onNext(state));
      else if (action === 'email-submit') el.addEventListener('submit', (e) => onEmailSubmit(state, e));
      else if (action === 'email-skip')   el.addEventListener('click', () => onEmailSkip(state));
      else if (action === 'restart') el.addEventListener('click', () => onRestart(state));
      else if (action === 'shop')    el.addEventListener('click', (e) => onShop(state, e, el));
      else if (action === 'avi-toggle') el.addEventListener('click', (e) => onAviToggle(state, e, el));
      else if (action === 'avi-close')  el.addEventListener('click', (e) => onAviClose(state, e, el));
    });
  }

  // ── Avatar intro card toggle ─────────────────────────────────────────────
  function expandAviPanel(panel) {
    panel.style.transition = 'none';
    panel.style.overflow = 'visible';
    panel.style.height = 'auto';
    const h = panel.scrollHeight;
    panel.style.height = '0px';
    panel.style.overflow = 'hidden';
    // force reflow
    void panel.offsetHeight;
    panel.style.transition = 'height 0.48s cubic-bezier(0.4,0,0.2,1)';
    panel.style.height = h + 'px';
    const cleanup = (e) => {
      if (e && e.propertyName && e.propertyName !== 'height') return;
      panel.style.transition = 'none';
      panel.style.height = 'auto';
      panel.style.overflow = 'visible';
      panel.removeEventListener('transitionend', cleanup);
    };
    panel.addEventListener('transitionend', cleanup);
  }
  function collapseAviPanel(panel) {
    panel.style.transition = 'none';
    panel.style.height = panel.scrollHeight + 'px';
    panel.style.overflow = 'hidden';
    void panel.offsetHeight;
    panel.style.transition = 'height 0.4s cubic-bezier(0.4,0,0.2,1)';
    panel.style.height = '0px';
  }
  function openAviCard(state, id) {
    const card = state.stage.querySelector('#ftq-avi-card-' + id);
    const panel = state.stage.querySelector('#ftq-avi-open-' + id);
    if (!card || !panel) return;
    card.classList.add('is-open');
    expandAviPanel(panel);
    state._aviOpenId = id;
    setTimeout(() => {
      try { card.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
    }, 120);
  }
  function closeAviCard(state, id) {
    const card = state.stage.querySelector('#ftq-avi-card-' + id);
    const panel = state.stage.querySelector('#ftq-avi-open-' + id);
    if (!card || !panel) return;
    card.classList.remove('is-open');
    collapseAviPanel(panel);
    if (state._aviOpenId === id) state._aviOpenId = null;
  }
  function onAviToggle(state, e, el) {
    // Ignore clicks on the close button (it has its own handler)
    if (e.target.closest('[data-ftq-action="avi-close"]')) return;
    const id = el.dataset.aviId;
    if (state._aviOpenId === id) {
      closeAviCard(state, id);
    } else {
      if (state._aviOpenId) closeAviCard(state, state._aviOpenId);
      openAviCard(state, id);
    }
  }
  function onAviClose(state, e, el) {
    e.stopPropagation();
    closeAviCard(state, el.dataset.aviId);
  }

  // ── Transitions / actions ────────────────────────────────────────────────
  function transition(state, mutate, dir) {
    if (state.leaving) return;
    state.leaving = true;
    state.dir = dir || 'forward';
    // Fade out the existing stage briefly, then re-render.
    state.stage.style.opacity = '0';
    state.stage.style.transition = 'opacity 0.19s ease';
    setTimeout(() => {
      // Stage is now invisible — scroll first so no visible page jump,
      // then mutate + render so the new screen's slide-in animation plays
      // cleanly without scroll movement competing with it.
      try {
        state.root.scrollIntoView({ block: 'start', behavior: 'instant' });
      } catch (_) {
        state.root.scrollIntoView({ block: 'start' });
      }
      mutate();
      state.leaving = false;
      state.stage.style.opacity = '';
      state.stage.style.transition = '';
      renderCurrent(state);
    }, 190);
  }

  function onSelect(state, idx) {
    state.answers[state.qIdx] = idx;
    const tiles = state.stage.querySelectorAll('.ftq-tile');
    tiles.forEach((tile, i) => {
      const wasSelected = tile.classList.contains('is-selected');
      const isSelected = i === idx;
      tile.classList.toggle('is-selected', isSelected);
      tile.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      const existingCheck = tile.querySelector('.ftq-tile-check');
      if (isSelected && !existingCheck) {
        const check = document.createElement('div');
        check.className = 'ftq-tile-check';
        check.textContent = '✦';
        tile.appendChild(check);
      } else if (!isSelected && existingCheck) {
        existingCheck.remove();
      }
      if (isSelected && !wasSelected) {
        // re-trigger pop animation
        tile.style.animation = 'none';
        void tile.offsetWidth;
        tile.style.animation = '';
      }
    });

    const cont = state.stage.querySelector('.ftq-btn-continue');
    if (cont) {
      cont.classList.add('is-active');
      cont.removeAttribute('disabled');
    }

    const q = state.content.questions[state.qIdx];
    const charKey = q.answers[idx] && q.answers[idx].char;
    if (charKey) {
      setLantern(state, charKey);
      showWhisper(state, charKey);
    }
  }

  function showWhisper(state, charKey) {
    const host = state.stage.querySelector('.ftq-whisper-host');
    if (!host) return;
    const text = pickWhisper(charKey, state.charsByKey);
    if (!text) return;

    if (state.whisperTimer) clearTimeout(state.whisperTimer);

    host.innerHTML = `
      <div class="ftq-whisper">
        <div class="ftq-whisper-fox">🦊</div>
        <div>
          <div class="ftq-whisper-txt">"${esc(text)}"</div>
          <span class="ftq-whisper-sig">— The Fox</span>
        </div>
      </div>
    `;

    state.whisperTimer = setTimeout(() => {
      const w = host.querySelector('.ftq-whisper');
      if (!w) return;
      w.classList.add('is-fading');
      setTimeout(() => { if (host.querySelector('.ftq-whisper') === w) host.innerHTML = ''; }, 380);
    }, 3200);
  }

  function onNext(state) {
    if (state.answers[state.qIdx] == null) return;
    const total = state.content.questions.length;
    if (state.qIdx < total - 1) {
      transition(state, () => { state.qIdx++; }, 'forward');
    } else {
      transition(state, () => { state.screen = 'email'; }, 'forward');
    }
  }

  function onBack(state) {
    if (state.screen !== 'quiz') return;
    if (state.qIdx === 0) {
      transition(state, () => { state.screen = 'intro'; }, 'back');
    } else {
      transition(state, () => { state.qIdx--; }, 'back');
    }
  }

  function onEmailSubmit(state, e) {
    e.preventDefault();
    // Re-entrancy guard. The handler is bound to the form's `submit` event, so
    // pressing Enter (not just clicking the button) can fire it twice; the first
    // valid submit calls transition() which sets state.leaving synchronously, so
    // this blocks any duplicate subscribe/dispatch within the transition window.
    if (state.leaving) return;
    const input = state.stage.querySelector('.ftq-email-input');
    const errEl = state.stage.querySelector('.ftq-email-error');
    const v = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      input.classList.add('is-error');
      errEl.textContent = 'Please enter a valid email address.';
      errEl.removeAttribute('hidden');
      return;
    }
    state.email = v;
    const result = dispatchSubmit(state, v);
    submitToKlaviyo(state, v, result);
    transition(state, () => { state.screen = 'result'; }, 'forward');
  }

  function onEmailSkip(state) {
    if (state.leaving) return; // same re-entrancy guard as onEmailSubmit
    // Event still fires for any analytics listener, but skipping means no email —
    // so there is nothing to subscribe to Klaviyo.
    dispatchSubmit(state, null);
    transition(state, () => { state.screen = 'result'; }, 'forward');
  }

  function dispatchSubmit(state, email) {
    const result = calcResult(state.answers, state.content.questions);
    state.root.dispatchEvent(new CustomEvent('foxtale-quiz:submit', {
      bubbles: true,
      detail: {
        email,
        winner: result.winner,
        secondary: result.secondary,
        resultType: result.resultType,
        counts: result.counts,
        properties: buildResultProps(state, result), // foxtale_* props (same set sent to Klaviyo)
        klaviyo: state.config.klaviyo,                // listener can use { publicKey, listId } too
      },
    }));
    return result;
  }

  // ── Klaviyo handoff ──────────────────────────────────────────────────────
  // The result-email template (context/forest-quiz/klaviyo-result-email.html)
  // branches on these foxtale_* profile properties. Keep the keys in sync with it.
  function buildResultProps(state, result) {
    const winner = state.charsByKey[result.winner];
    const secondary = state.charsByKey[result.secondary];
    return {
      foxtale_result_type:    result.resultType,
      foxtale_result_key:     result.winner,
      foxtale_secondary_key:  result.secondary,
      foxtale_blend_key:      blendKeyFor(result.winner, result.secondary),
      foxtale_girl:           (winner && winner.name) || '',
      foxtale_secondary_girl: (secondary && secondary.name) || '',
    };
  }

  // Subscribes the taker to the Klaviyo list (single opt-in) with their archetype
  // properties, via the public client endpoint. One call covers subscribe + consent
  // + list membership + custom properties — verified against klaviyo-openapi.json
  // (revision 2026-04-15, POST /client/subscriptions). Fire-and-forget: never blocks
  // or fails the result reveal; the on-screen result is the primary payoff.
  function submitToKlaviyo(state, email, result) {
    if (!email) return; // skip path — nothing to subscribe
    const kl = (state.config && state.config.klaviyo) || {};
    if (!kl.publicKey || !kl.listId) {
      console.warn('[foxtale-quiz] Klaviyo publicKey/listId not configured — skipping subscribe');
      return;
    }
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Which Foxtale Girl Are You',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: buildResultProps(state, result),
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: kl.listId } } },
      },
    };
    const url = 'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(kl.publicKey);
    const opts = {
      method: 'POST',
      headers: {
        'revision': '2026-04-15',
        'content-type': 'application/vnd.api+json',
        'accept': 'application/vnd.api+json',
      },
      body: JSON.stringify(body),
      keepalive: true,
    };
    const attempt = (retry) => fetch(url, opts).then((res) => {
      if (!res.ok) { // any non-2xx (Klaviyo returns 202 on success)
        console.warn('[foxtale-quiz] Klaviyo subscribe returned HTTP', res.status);
        if (retry && res.status >= 500) return attempt(false); // one retry on server error
      }
    }).catch((err) => {
      console.warn('[foxtale-quiz] Klaviyo subscribe failed:', err);
      if (retry) return attempt(false); // one retry on network error
    });
    attempt(true);
  }

  function onRestart(state) {
    if (state.whisperTimer) clearTimeout(state.whisperTimer);
    state.resultPhaseTimers.forEach(clearTimeout);
    state.resultPhaseTimers = [];
    state.overlay.style.background = 'rgba(5,14,10,0)';
    state.overlay.classList.remove('is-blocking');
    transition(state, () => {
      state.answers = new Array(state.content.questions.length).fill(null);
      state.qIdx = 0;
      state.email = '';
      state.screen = 'intro';
    }, 'back');
  }

  function onShop(state, e, el) {
    // Default <a href> handles navigation. Nothing to do.
  }

  // ── Result phase orchestration ───────────────────────────────────────────
  function startResultPhases(state) {
    const result = state._result;
    const winnerCfg = state.config.characters && state.config.characters[result.winner] || {};
    const floodColor = hexToRgba(winnerCfg.flood || winnerCfg.accent, 0.3) || 'rgba(95,138,166,0.3)';
    const resultEl = state.stage.querySelector('.ftq-result');

    setLantern(state, result.winner);

    // Phase 0. Overlay flood/dim phases removed — they were designed to
    // run on a fullscreen overlay that the now-removed `:empty` rule had
    // been hiding the whole time, so this sequence had never rendered.
    // Content fades in via the .ftq-phase-1/.ftq-phase-2 opacity classes.
    if (resultEl) resultEl.dataset.phase = '0';

    state.resultPhaseTimers.forEach(clearTimeout);
    state.resultPhaseTimers = [];

    // t=700ms: firefly burst from emblem(s) + portrait fades up
    state.resultPhaseTimers.push(setTimeout(() => {
      const origins = state.stage.querySelectorAll('.ftq-result-emblem');
      const burstParent = state.forest || document.body;
      // Blend + constellation: one burst per emblem. Primary: just the first.
      const isMulti = result.resultType === 'blend' || result.resultType === 'constellation';
      const targets = isMulti ? origins : (origins.length ? [origins[0]] : []);
      targets.forEach(o => spawnFireflyBurst(o, burstParent));
      if (resultEl) resultEl.classList.add('is-portrait-visible');
    }, 700));

    // t=900ms: phase 1 — identity (name, eyebrow, badge) fades in
    state.resultPhaseTimers.push(setTimeout(() => {
      if (resultEl) resultEl.dataset.phase = '1';
    }, 900));

    // t=1700ms: phase 2 — body content fades in, mix bars animate
    state.resultPhaseTimers.push(setTimeout(() => {
      if (resultEl) resultEl.dataset.phase = '2';
      state.stage.querySelectorAll('.ftq-mix-bar').forEach(bar => {
        bar.style.width = (bar.dataset.targetWidth || 0) + '%';
      });
    }, 1700));
  }

  // ── Dev preview (URL param) ──────────────────────────────────────────────
  // ?preview=dani                       → primary, winner=dani
  // ?preview=dani:pax                   → blend, winner=dani, secondary=pax
  // ?preview=dani:pax:kez[:...]         → constellation (3+ chars)
  function parsePreviewParam() {
    const KEYS = CHAR_ORDER;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('preview');
    if (!raw) return null;

    const chars = raw.split(':').filter(k => KEYS.indexOf(k) !== -1);

    if (chars.length === 1) {
      const winner = chars[0];
      const counts = {};
      KEYS.forEach(k => { counts[k] = k === winner ? 8 : 1; });
      const secondary = KEYS.filter(k => k !== winner)[0];
      return { winner: winner, secondary: secondary, resultType: 'primary', counts: counts };
    }
    if (chars.length === 2) {
      const counts = {};
      KEYS.forEach(k => {
        counts[k] = k === chars[0] ? 4 : k === chars[1] ? 3 : 1;
      });
      return { winner: chars[0], secondary: chars[1], resultType: 'blend', counts: counts };
    }
    if (chars.length >= 3) {
      const counts = {};
      KEYS.forEach(k => { counts[k] = chars.indexOf(k) !== -1 ? 2 : 0; });
      return { winner: chars[0], secondary: chars[1], resultType: 'constellation', counts: counts };
    }
    return null;
  }

  // ── Init ─────────────────────────────────────────────────────────────────
  function init(root) {
    const sectionId = root.dataset.sectionId;
    const contentEl = document.getElementById('ftq-content');
    const configEl = document.getElementById('ftq-config-' + sectionId);
    if (!contentEl || !configEl) {
      console.warn('[foxtale-quiz] missing content or config script');
      return;
    }

    let content, config;
    try {
      content = JSON.parse(contentEl.textContent);
      config = JSON.parse(configEl.textContent);
    } catch (err) {
      console.error('[foxtale-quiz] JSON parse failed:', err);
      return;
    }

    const charsByKey = {};
    content.characters.forEach(c => { charsByKey[c.key] = c; });

    const state = {
      root,
      content,
      config,
      charsByKey,
      stage: root.querySelector('.ftq-stage'),
      progress: document.getElementById('ftq-progress-line-' + sectionId),
      backBtn: document.getElementById('ftq-back-btn-' + sectionId),
      lantern: root.querySelector('.ftq-lantern'),
      forest: root.querySelector('.ftq-forest'),
      overlay: root.querySelector('.ftq-result-overlay'),
      // state
      screen: 'intro',
      qIdx: 0,
      answers: new Array(content.questions.length).fill(null),
      leaving: false,
      dir: 'forward',
      email: '',
      whisperTimer: null,
      resultPhaseTimers: [],
      reducedMotion: false,
      _result: null,
    };

    state.reducedMotion = false;
    root.dataset.reduceMotion = 'false';

    console.log('[foxtale-quiz] init', { sectionId: sectionId, fireflies: config.fireflies, characters: content.characters.length });

    if (config.fireflies) {
      try {
        respawnFireflies(root, config.fireflies);
        console.log('[foxtale-quiz] fireflies spawned mid+near=', root.querySelectorAll('.ftq-flies .ftq-p').length);
      } catch (e) {
        console.error('[foxtale-quiz] firefly init failed:', e);
      }
      startCardGlowTracker(root);
    } else {
      console.warn('[foxtale-quiz] no firefly config');
    }

    state.backBtn.addEventListener('click', () => onBack(state));

    // Dev preview: ?preview=<key> | ?preview=k1:k2 | ?preview=k1:k2:k3[:...]
    const preview = parsePreviewParam();
    if (preview) {
      state._previewResult = preview;
      state.screen = 'result';
      console.log('[foxtale-quiz] preview override', preview);
    }

    // Dev shortcut: ?screen=intro|quiz|email|result jumps straight to that screen
    const screenParam = new URLSearchParams(window.location.search).get('screen');
    if (screenParam && ['intro', 'quiz', 'email', 'result'].indexOf(screenParam) !== -1) {
      state.screen = screenParam;
      console.log('[foxtale-quiz] screen override', screenParam);
    }

    renderCurrent(state);

    root.dataset.ftqReady = 'true';
  }

  function boot() {
    document.querySelectorAll('[data-foxtale-quiz]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
